import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useAuth } from 'src/contexts/providers/AuthProvider'
import {
  ResCenterMigrationBannerState,
  ResCenterMigrationTryItResult,
  TryItOutcome,
  openLearnMore,
  submitTryIt,
} from 'src/helpers/resCenterMigrationBanner'
import {
  RECORD_RES_CENTER_MIGRATION_LEARN_MORE,
  TRY_RES_CENTER_MIGRATION,
} from './data/mutations'
import { GET_RES_CENTER_MIGRATION_BANNER } from './data/queries'
import { ResCenterMigrationBannerView } from './ResCenterMigrationBannerView'

const ResCenterMigrationBanner: FC = () => {
  const { isAuthenticated } = useAuth()
  const { data, loading } = useQuery<{
    resCenterMigrationBanner: ResCenterMigrationBannerState
  }>(GET_RES_CENTER_MIGRATION_BANNER, { skip: !isAuthenticated })

  const writeState = useCallback(
    (cache, state: ResCenterMigrationBannerState) =>
      cache.writeQuery({
        query: GET_RES_CENTER_MIGRATION_BANNER,
        data: { resCenterMigrationBanner: state },
      }),
    [],
  )

  const [recordLearnMore] = useMutation<{
    recordResCenterMigrationLearnMore: ResCenterMigrationBannerState
  }>(RECORD_RES_CENTER_MIGRATION_LEARN_MORE, {
    update: (cache, { data: result }) => {
      if (result) writeState(cache, result.recordResCenterMigrationLearnMore)
    },
  })
  const [tryResCenterMigration] = useMutation<{
    tryResCenterMigration: ResCenterMigrationTryItResult
  }>(TRY_RES_CENTER_MIGRATION, {
    update: (cache, { data: result }) => {
      if (result) writeState(cache, result.tryResCenterMigration.state)
    },
  })

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [learnMoreFailed, setLearnMoreFailed] = useState(false)
  const [outcome, setOutcome] = useState<TryItOutcome | null>(null)
  const submitLock = useRef(false)
  const learnMoreLock = useRef(false)

  const state = data?.resCenterMigrationBanner

  const handleLearnMore = useCallback(async () => {
    if (!state || learnMoreLock.current) return
    learnMoreLock.current = true
    setLearnMoreFailed(false)
    try {
      const result = await openLearnMore({
        state,
        recordLearnMore: () => recordLearnMore(),
        openUrl: (url) => window.open(url, '_blank', 'noopener,noreferrer'),
      })
      if (!result.ok) setLearnMoreFailed(true)
    } finally {
      learnMoreLock.current = false
    }
  }, [state, recordLearnMore])

  useEffect(() => {
    const resetAfterNavigation = () => {
      submitLock.current = false
      setSubmitting(false)
    }
    window.addEventListener('pageshow', resetAfterNavigation)
    return () => window.removeEventListener('pageshow', resetAfterNavigation)
  }, [])

  const handleConfirm = useCallback(async () => {
    setConfirmOpen(false)
    setOutcome(null)
    setSubmitting(true)
    const result = await submitTryIt({
      lock: submitLock,
      tryIt: async () => {
        const response = await tryResCenterMigration()
        if (!response.data) throw new Error('Missing try-it response')
        return response.data.tryResCenterMigration
      },
      fallbackResCenterUrl: state?.res_center_url,
      navigate: (url) => window.location.assign(url),
    })
    if (result.kind === 'ignored') return
    setOutcome(result)
    if (result.kind !== 'navigate') setSubmitting(false)
  }, [state?.res_center_url, tryResCenterMigration])

  if (loading && !data) return null

  return (
    <ResCenterMigrationBannerView
      state={state}
      confirmOpen={confirmOpen}
      submitting={submitting}
      learnMoreFailed={learnMoreFailed}
      outcome={outcome}
      onLearnMore={handleLearnMore}
      onTryIt={() => setConfirmOpen(true)}
      onCancel={() => setConfirmOpen(false)}
      onConfirm={handleConfirm}
    />
  )
}

export default ResCenterMigrationBanner
